import { handleResponse } from './errorHandler';
import AccessTokenManager from './accessTokenManager';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export interface SOWConversationResponse {
  conversation: any;
  conversationResponse: {
    outstandingQuestion?: {
      QuestionText: string;
      Options: string[];
      NeedPicture: boolean;
    };
    workItemsForConfirmation?: {
      category: string;
      description: string;
      reasoning: string;
    }[];
    confirmationQuestion?: string;
    Answer?: any;
    // Work item refinement specific fields
    refinementComplete?: boolean;
    refinedDetails?: {
      scope: string;
      specifications: string[];
      estimationReady: boolean;
    };
    summary?: string;
    HaveAnswer?: boolean;
    // Batch processing fields
    questionBatch?: {
      questions: any[];
    };
    batchProgress?: {
      current: number;
      total: number;
    };
  };
}

export interface QuestionnaireQuestion {
  id: string;
  question: string;
  options: string[];
  category: string;
  priority: number;
}

export interface QuestionnaireResponse {
  projectType: string;
  questions: QuestionnaireQuestion[];
}

export interface QuestionnaireAnswer {
  questionId: string;
  answer: string;
  imageIds?: string[]; // References to images used for this answer
}

export interface QuestionnaireSubmissionResponse {
  workItems: {
    category: string;
    description: string;
    reasoning: string;
  }[];
  summary: string;
  status: string;
}

export const sowConversationService = {
  // Start a new SOW conversation
  async startConversation(sowId: string, initialDescription?: string, imageAnalysis?: any): Promise<SOWConversationResponse> {
    try {
      const token = AccessTokenManager.getAccessToken();
      
      if (!token) {
        throw new Error('No auth token available');
      }
      
      const response = await fetch(`${API_BASE_URL}/statementofwork/${sowId}/conversation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          initialDescription,
          imageAnalysis
        }),
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('SOW conversation service error:', error);
      throw error;
    }
  },

  // Continue existing SOW conversation
  async continueConversation(conversationId: string, conversationData: any): Promise<SOWConversationResponse> {
    try {
      const token = AccessTokenManager.getAccessToken();
      
      if (!token) {
        throw new Error('No auth token available');
      }
      
      const response = await fetch(`${API_BASE_URL}/statementofwork/conversation/${conversationId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(conversationData),
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('SOW conversation service error:', error);
      throw error;
    }
  },

  // Get existing SOW conversation
  async getConversation(conversationId: string): Promise<any> {
    try {
      const token = AccessTokenManager.getAccessToken();
      
      if (!token) {
        throw new Error('No auth token available');
      }
      
      const response = await fetch(`${API_BASE_URL}/statementofwork/conversation/${conversationId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('SOW conversation service error:', error);
      throw error;
    }
  },

  // Get structured questionnaire for SOW
  async getQuestionnaire(sowId: string): Promise<QuestionnaireResponse> {
    try {
      const token = AccessTokenManager.getAccessToken();
      
      if (!token) {
        throw new Error('No auth token available');
      }
      
      const response = await fetch(`${API_BASE_URL}/statementofwork/${sowId}/questionnaire`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('SOW questionnaire service error:', error);
      throw error;
    }
  },

  // Save questionnaire progress (individual answers)
  async saveQuestionnaireProgress(sowId: string, answers: QuestionnaireAnswer[]): Promise<{ status: string; totalAnswered: number }> {
    try {
      const token = AccessTokenManager.getAccessToken();
      
      if (!token) {
        throw new Error('No auth token available');
      }
      
      const response = await fetch(`${API_BASE_URL}/statementofwork/${sowId}/questionnaire/progress`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          answers
        }),
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('SOW questionnaire progress service error:', error);
      throw error;
    }
  },

  // Submit completed questionnaire
  async submitQuestionnaire(sowId: string, answers: QuestionnaireAnswer[]): Promise<QuestionnaireSubmissionResponse> {
    try {
      const token = AccessTokenManager.getAccessToken();
      
      if (!token) {
        throw new Error('No auth token available');
      }
      
      const response = await fetch(`${API_BASE_URL}/statementofwork/${sowId}/questionnaire`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          answers
        }),
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('SOW questionnaire submission service error:', error);
      throw error;
    }
  },

  // Start work item refinement
  async startWorkItemRefinement(sowId: string, workItemId: string): Promise<SOWConversationResponse> {
    try {
      const token = AccessTokenManager.getAccessToken();
      
      if (!token) {
        throw new Error('No auth token available');
      }
      
      const response = await fetch(`${API_BASE_URL}/statementofwork/${sowId}/workitem/${workItemId}/refinement`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('Work item refinement start service error:', error);
      throw error;
    }
  },

  // Continue work item refinement conversation
  async continueWorkItemRefinement(conversationId: string, conversationData: any): Promise<SOWConversationResponse> {
    try {
      const token = AccessTokenManager.getAccessToken();
      
      if (!token) {
        throw new Error('No auth token available');
      }
      
      const response = await fetch(`${API_BASE_URL}/statementofwork/workitem/refinement/${conversationId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(conversationData),
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('Work item refinement continue service error:', error);
      throw error;
    }
  },

  // Associate image with work item
  async associateWorkItemImage(sowId: string, workItemId: string, imageId: string, analysisContext: string): Promise<any> {
    try {
      const token = AccessTokenManager.getAccessToken();
      
      if (!token) {
        throw new Error('No auth token available');
      }
      
      const response = await fetch(`${API_BASE_URL}/statementofwork/${sowId}/workitem/${workItemId}/image/${imageId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          analysisContext
        }),
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('Work item image association service error:', error);
      throw error;
    }
  },

  // NEW ARCHITECTURE: Questionnaire methods
  async getQuestionnaires(sowId: string): Promise<{ questionnaires: any[]; workItems: any[] }> {
    try {
      const token = AccessTokenManager.getAccessToken();
      
      if (!token) {
        throw new Error('No auth token available');
      }
      
      const response = await fetch(`${API_BASE_URL}/statementofwork/${sowId}/questionnaires`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('Get questionnaires service error:', error);
      throw error;
    }
  },

  async startQuestionnaire(sowId: string, questionnaireId: string): Promise<any> {
    try {
      const token = AccessTokenManager.getAccessToken();
      
      if (!token) {
        throw new Error('No auth token available');
      }
      
      const response = await fetch(`${API_BASE_URL}/statementofwork/${sowId}/questionnaire/${questionnaireId}/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('Start questionnaire service error:', error);
      throw error;
    }
  },

  async completeQuestionnaire(sowId: string, questionnaireId: string, answers: any[]): Promise<any> {
    try {
      const token = AccessTokenManager.getAccessToken();
      
      if (!token) {
        throw new Error('No auth token available');
      }
      
      const response = await fetch(`${API_BASE_URL}/statementofwork/${sowId}/questionnaire/${questionnaireId}/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          answers
        }),
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('Complete questionnaire service error:', error);
      throw error;
    }
  }
};